import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Send } from "lucide-react";
import { LOGO } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70";
const label = "block text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2";

export default function FieldDuty() {
  const [form, setForm] = useState({ email: "", date: "", check_in: "", check_out: "", customer: "", site: "", work_order: "", purpose: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.check_out) delete payload.check_out;
      if (!payload.work_order) delete payload.work_order;
      const { data } = await axios.post(`${API}/field-duty/log`, payload);
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record field duty");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center px-6 py-16" data-testid="field-duty-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-12 w-auto mx-auto mb-6" />
          <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">Field & Installation Team</p>
          <h1 className="font-marcellus text-3xl text-white mt-3">Field Duty Check-in</h1>
          <p className="text-sm text-slate-500 mt-3">Working at a customer site instead of the office? Record your duty here — your attendance is updated automatically.</p>
        </div>

        {done ? (
          <div className="border border-emerald-500/30 bg-emerald-500/10 p-8 text-center" data-testid="field-duty-success">
            <CheckCircle2 size={32} className="text-emerald-300 mx-auto mb-4" />
            <p className="text-white text-lg">Field duty recorded</p>
            <p className="text-sm text-slate-400 mt-2">Your attendance has been updated{done.ot_minutes ? ` and ${done.ot_minutes} minutes of overtime is pending manager approval` : ""}{done.holiday_work ? ". Holiday work is eligible for comp-off" : ""}.</p>
            <a href="/my-requests" className="inline-block mt-5 text-xs text-nx-goldlight hover:text-white transition-colors border border-nx-gold/40 px-4 py-2" data-testid="fd-track-link">Track this entry anytime → My Requests</a>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-7 space-y-5">
            <label className="block">
              <span className={label}>Your email registered with HR</span>
              <input type="email" required className={input} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="fd-email" />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <span className={label}>Date</span>
                <input type="date" required className={input} value={form.date} onChange={set("date")} data-testid="fd-date" />
              </label>
              <label className="block">
                <span className={label}>Check-in</span>
                <input type="time" required className={input} value={form.check_in} onChange={set("check_in")} data-testid="fd-checkin" />
              </label>
              <label className="block">
                <span className={label}>Check-out (if done)</span>
                <input type="time" className={input} value={form.check_out} onChange={set("check_out")} data-testid="fd-checkout" />
              </label>
            </div>
            <label className="block">
              <span className={label}>Customer</span>
              <input required minLength={2} className={input} value={form.customer} onChange={set("customer")} placeholder="Customer / company name" data-testid="fd-customer" />
            </label>
            <label className="block">
              <span className={label}>Site / location</span>
              <input required minLength={2} className={input} value={form.site} onChange={set("site")} placeholder="Site address or area" data-testid="fd-site" />
            </label>
            <label className="block">
              <span className={label}>Work order / job reference (optional)</span>
              <input className={input} value={form.work_order} onChange={set("work_order")} placeholder="e.g. WO-1024" data-testid="fd-workorder" />
            </label>
            <label className="block">
              <span className={label}>Job purpose</span>
              <textarea required minLength={5} rows={3} className={input} value={form.purpose} onChange={set("purpose")} placeholder="e.g. CCTV installation — camera mounting and cabling" data-testid="fd-purpose" />
            </label>
            <button type="submit" disabled={busy} className="w-full bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3.5 transition-colors disabled:opacity-50" data-testid="fd-submit">
              <Send size={13} className="inline mr-1" /> {busy ? "Recording…" : "Record Field Duty"}
            </button>
            <p className="text-[11px] text-slate-600 text-center"><MapPin size={11} className="inline mr-1" /> Work past 5:30 PM or on holidays goes to your manager for overtime / comp-off approval.</p>
          </form>
        )}
      </div>
    </div>
  );
}
